import { HelmetDexteel, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2 as Grid } from "@mui/material";
import { GridApi, GridReadyEvent, themeMaterial } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGridStyles } from "../../styles/gridStyles";
import { CreateHeatModal } from "./CreateModal/CreateHeatModal";
import { DeleteHeatModal } from "./DeleteModal/DeleteHeatModal";
import { EditHeatModal } from "./EditModal/EditHeatModal";
import NewHeatStatusBar from "./buttons/NewHeat";
import { useHeatsOptionsFunctions } from "./hooks/useHeatsOptionsFunctions";
import { useTableData } from "./hooks/useTableData";

type Props = {
	isLoading: boolean;
	getHeatsByShift: Function;
	gridRef: React.RefObject<AgGridReact>;
};

export const HeatsByShiftTable = ({
	isLoading,
	getHeatsByShift,
	gridRef,
}: Props) => {
	const [modalEditHeat, setModalEditHeat] = useState(false);
	const [modalDeleteHeat, setModalDeleteHeat] = useState(false);
	const [modalCreateHeat, setModalCreateHeat] = useState(false);
	const [heatIdForEdit, setHeatIdForEdit] = useState<number | null>(null);
	const [heatIdForDelete, setHeatIdForDelete] = useState<number | null>(null);
	const [selectedHeatData, setSelectedHeatData] = useState<any>(null);
	const [gridAPI, setGridAPI] = useState<GridApi | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();
	const classes = useGridStyles();

	const { getMenuOptions, goToDataEntry, goToEAFDataEntry, goToHeatReport } =
		useHeatsOptionsFunctions({
			setHeatIdForEdit,
			setModalEditHeat,
			setHeatIdForDelete,
			setModalDeleteHeat,
			setModalCreateHeat,
		});
	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { rowData, columnData, defaultColDef } = useTableData({
		setHeatIdForEdit,
		setModalEditHeat,
		setHeatIdForDelete,
		setModalDeleteHeat,
		setModalCreateHeat,
		goToDataEntry,
		goToEAFDataEntry,
		goToHeatReport,
	});

	const onGridReady = (params: GridReadyEvent) => {
		setGridAPI(params.api);
	};
	const onSelectionChanged = () => {
		if (!gridAPI) return;
		const selectedRows = gridAPI.getSelectedRows();
		if (selectedRows.length > 0) {
			setSelectedHeatData(selectedRows[0]);
			setSearchParams({ heatId: selectedRows[0].HeatId }, { replace: true });
		} else {
			setSelectedHeatData(null);
			setSearchParams({}, { replace: true });
		}
	};

	const heatIdSearch = searchParams.get("heatId");

	useEffect(() => {
		if (gridAPI) {
			if (isLoading) gridAPI.showLoadingOverlay();
			else gridAPI.hideOverlay();
		}
	}, [gridAPI, isLoading]);

	useEffect(() => {
		if (!isLoading && gridAPI && rowData && rowData.length > 0) {
			const heatId = heatIdSearch;

			if (heatId) {
				const node = gridAPI.getRowNode(heatId);
				if (node) {
					gridAPI.deselectAll();
					node?.setSelected(true, true);
					setSelectedHeatData(node.data);
				}
			}
		}
	}, [gridAPI, isLoading, rowData]);

	const frameworkComponents = {
		newHeatStatusBar: NewHeatStatusBar,
	};
	const statusBarConfig = {
		statusPanels: [
			{
				statusPanel: "newHeatStatusBar",
				align: "right",
				statusPanelParams: { onNewHeat: () => setModalCreateHeat(true) },
			},
		],
	};

	useEffect(() => {
		registerConfig({
			id: "heatsByShiftTable",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			{selectedHeatData &&
				!modalEditHeat &&
				!modalDeleteHeat &&
				!modalCreateHeat && (
					<HelmetDexteel title={`${selectedHeatData.HeatName} - Heats`} />
				)}
			<Grid container>
				<Grid
					size={{ xs: 12, md: 12 }}
					style={{
						border: "1px solid #e0e0e0",
						minHeight: "65vh",
					}}
					onContextMenu={(event) => {
						event.preventDefault();
						showContextMenu(event, null, "heatsByShiftTable");
					}}
				>
					<DeleteHeatModal
						heatIdForDelete={heatIdForDelete}
						show={modalDeleteHeat}
						onHide={(shouldUpdate: boolean) => {
							setModalDeleteHeat(false);
							if (shouldUpdate) getHeatsByShift().then(() => {});
						}}
					/>{" "}
					<EditHeatModal
						heatIdForEdit={heatIdForEdit}
						show={modalEditHeat}
						onHide={(shouldUpdate: boolean) => {
							setModalEditHeat(false);
							if (shouldUpdate) getHeatsByShift().then(() => {});
						}}
					/>
					<CreateHeatModal
						show={modalCreateHeat}
						onHide={(shouldUpdate: boolean) => {
							setModalCreateHeat(false);
							if (shouldUpdate) getHeatsByShift().then(() => {});
						}}
					/>
					<AgGridReact
						onGridReady={onGridReady}
						rowData={rowData}
						columnDefs={columnData}
						rowSelection="single"
						overlayLoadingTemplate={"Loading..."}
						suppressContextMenu={true}
						getRowId={(params) => params.data.HeatId}
						onRowDoubleClicked={(event: any) => {
							goToDataEntry(event.data.HeatNameLink);
						}}
						onCellContextMenu={(event) => {
							if (event.event)
								showContextMenu(
									event.event as any,
									event.data,
									"heatsByShiftTable",
								);
						}}
						onSelectionChanged={onSelectionChanged}
						ref={gridRef}
						statusBar={statusBarConfig}
						components={frameworkComponents}
					/>
				</Grid>
			</Grid>
		</>
	);
};
